/**
 * 快捷键编辑器（M5-03 / DESIGN.md §7.4）。
 *
 * 列出全部已注册动作，点击「录制」进入录制态，按下一个真实组合键即改绑；
 * 新组合键已经被别的动作占用时不静默覆盖，先问一遍。纯展示组件：数据
 * （动作列表/当前覆盖）进，改动过回调出，不认识 `ShortcutOverrideStore`
 * 本身（分层规则同 `LayersPanel`）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createMemo, createSignal } from 'solid-js';
import type { ActionDefinition } from '../../app/actions/registry';
import { fuzzyMatch } from '../../app/actions/registry';
import {
  formatShortcut,
  shortcutFromEvent,
  type Platform,
  type Shortcut,
} from '../../app/actions/keymap';
import { effectiveShortcut, findShortcutConflict, type ShortcutOverrides } from '../../app/actions/shortcut-overrides';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './ShortcutEditorDialog.module.css';

/** "录制"按钮的真实 DOM id（不是 `data-testid`——那是测试专用标记，
 * 这里要在生产代码里按 id 把焦点找回来，两件事不共用一个属性）。 */
function recordButtonId(actionId: string): string {
  return `shortcut-record-${actionId}`;
}

export interface ShortcutEditorDialogProps {
  readonly open: boolean;
  readonly actions: readonly ActionDefinition[];
  readonly overrides: ShortcutOverrides;
  readonly onClose: () => void;
  readonly onSetShortcut: (actionId: string, shortcut: Shortcut | null) => void;
  readonly onResetOne: (actionId: string) => void;
  readonly onResetAll: () => void;
  readonly platform?: Platform | undefined;
}

interface PendingConflict {
  readonly actionId: string;
  readonly shortcut: Shortcut;
  readonly conflictWith: ActionDefinition;
}

export function ShortcutEditorDialog(props: ShortcutEditorDialogProps): JSX.Element {
  const [query, setQuery] = createSignal('');
  const [recordingId, setRecordingId] = createSignal<string | null>(null);
  const [pendingConflict, setPendingConflict] = createSignal<PendingConflict | null>(null);

  let dialogRef: HTMLDivElement | undefined;
  // 录制中的 Esc 完全不会冒泡到这里——`onRecordKeyDown` 会 stopPropagation
  // 拦下（见那边的注释：不拦的话录制中按下的任意键会一路冒泡到
  // `main.tsx` 的全局键盘分发，被刚刚才提交的新绑定当场触发一次真实
  // 动作，等于"录制"这个动作本身在录我的时候就已经把自己执行了）。
  // 所以这里能收到 Escape，只可能是"当前没有在录制"，直接关对话框
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  /** 结束录制态并把焦点还给触发它的"录制"按钮——录制中的那个 `<span>`
   * 是随 `recordingId()` 条件渲染出来的，一清空就从 DOM 里消失，焦点如果
   * 不主动接回来会掉到 `document.body`：body 不在对话框子树内，之后
   * 任何键盘事件（哪怕是再按一次 Esc 关对话框）都不会冒泡到这里的
   * 焦点陷阱监听器，表现为"看起来卡住了，Esc 怎么按都没反应"。 */
  const stopRecording = (actionId: string): void => {
    setRecordingId(null);
    queueMicrotask(() => {
      document.getElementById(recordButtonId(actionId))?.focus();
    });
  };

  const filtered = createMemo(() => {
    const q = query().trim();
    if (q === '') return props.actions;
    return props.actions
      .map((action) => ({ action, score: fuzzyMatch(q, action.title) }))
      .filter((entry): entry is { action: ActionDefinition; score: number } => entry.score !== null)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.action);
  });

  const startRecording = (actionId: string): void => {
    setPendingConflict(null);
    setRecordingId(actionId);
  };

  const commitShortcut = (actionId: string, shortcut: Shortcut): void => {
    const conflictWith = findShortcutConflict(props.actions, props.overrides, actionId, shortcut);
    if (conflictWith !== null) {
      setPendingConflict({ actionId, shortcut, conflictWith: conflictWith });
      stopRecording(actionId);
      return;
    }
    props.onSetShortcut(actionId, shortcut);
    stopRecording(actionId);
  };

  const onRecordKeyDown = (actionId: string, event: KeyboardEvent): void => {
    event.preventDefault();
    // 必须拦住冒泡：录制期间按下的每一个键都不能被当成"真的在触发这个
    // 快捷键"——`main.tsx` 的全局键盘分发在 `window` 上，事件会一路冒泡
    // 过去；如果不拦，录制 Ctrl+Y 的同一下按键，一旦提交（`commitShortcut`
    // 已经同步写完覆盖记录），冒泡到 `window` 时就会被新写好的绑定当场
    // 匹配并执行一次，等于"录制"这个动作把自己刚录好的快捷键立刻触发了
    // 一遍（真实复现过：录制"全部"到 Ctrl+Y，画面在录制的瞬间就已经全选了）
    event.stopPropagation();
    if (event.key === 'Escape') {
      stopRecording(actionId);
      return;
    }
    const shortcut = shortcutFromEvent(event);
    if (shortcut === null) return; // 单独的修饰键，继续等主键
    commitShortcut(actionId, shortcut);
  };

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="shortcut-editor-backdrop">
        <div
          ref={(el) => {
            dialogRef = el;
          }}
          class={styles.dialog}
          role="dialog"
          aria-label="键盘快捷键"
          data-testid="shortcut-editor-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <header class={styles.header}>
            <h2 class={styles.title}>键盘快捷键</h2>
            <button
              type="button"
              class={styles.close}
              onClick={props.onClose}
              aria-label="关闭"
              data-testid="shortcut-editor-close"
            >
              ✕
            </button>
          </header>

          <div class={styles.toolbar}>
            <input
              class={styles.filter}
              type="search"
              placeholder="筛选动作"
              value={query()}
              onInput={(e) => setQuery(e.currentTarget.value)}
              data-testid="shortcut-editor-filter"
            />
            <button
              type="button"
              class={styles.resetAll}
              onClick={props.onResetAll}
              disabled={props.overrides.size === 0}
              data-testid="shortcut-editor-reset-all"
            >
              全部恢复默认
            </button>
          </div>

          <Show when={pendingConflict()}>
            {(conflict) => (
              <div class={styles.conflictBanner} data-testid="shortcut-editor-conflict">
                <span>
                  「{formatShortcut(conflict().shortcut, props.platform)}」已经被「{conflict().conflictWith.title}」占用，覆盖它？
                </span>
                <div class={styles.conflictActions}>
                  <button
                    type="button"
                    data-testid="shortcut-editor-conflict-override"
                    onClick={() => {
                      const c = conflict();
                      props.onSetShortcut(c.actionId, c.shortcut);
                      setPendingConflict(null);
                    }}
                  >
                    覆盖对方
                  </button>
                  <button
                    type="button"
                    data-testid="shortcut-editor-conflict-cancel"
                    onClick={() => setPendingConflict(null)}
                  >
                    取消
                  </button>
                </div>
              </div>
            )}
          </Show>

          <div class={styles.listWrap}>
            <table class={styles.table} data-testid="shortcut-editor-table">
              <thead>
                <tr>
                  <th>动作</th>
                  <th>分组</th>
                  <th>快捷键</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For each={filtered()}>
                  {(action) => {
                    const current = (): Shortcut | undefined => effectiveShortcut(action, props.overrides);
                    const overridden = (): boolean => props.overrides.has(action.id);
                    return (
                      <tr>
                        <td>{action.submenu === undefined ? action.title : `${action.submenu} > ${action.title}`}</td>
                        <td class={styles.group}>{action.group}</td>
                        <td>
                          <Show
                            when={recordingId() !== action.id}
                            fallback={
                              <span
                                class={styles.recording}
                                tabIndex={0}
                                data-testid={`shortcut-editor-recording-${action.id}`}
                                ref={(el) => {
                                  // 手动 `addEventListener` 而不是 Solid 的
                                  // `onKeyDown` prop：keydown 是 Solid 默认
                                  // 委托的事件之一，委托监听器挂在应用挂载根
                                  // 那一层，真正调用这里的回调发生在原生事件
                                  // 已经冒泡到根之后——比 `focus-trap.ts`
                                  // 挂在对话框根节点上的原生监听器晚，
                                  // `stopPropagation()` 根本来不及拦。手动
                                  // 挂原生监听器就在真实冒泡路径的正确位置上，
                                  // 真的能抢在对话框的 Esc 处理之前拦下（真实
                                  // 复现过：录制中按 Esc，对话框直接关掉了，
                                  // 因为委托版处理器压根没机会先跑）
                                  el.addEventListener('keydown', (e) => onRecordKeyDown(action.id, e));
                                  queueMicrotask(() => el.focus());
                                }}
                                onBlur={() => setRecordingId(null)}
                              >
                                按下组合键…（Esc 取消）
                              </span>
                            }
                          >
                            <span data-testid={`shortcut-editor-current-${action.id}`}>
                              {current() === undefined ? '—' : formatShortcut(current()!, props.platform)}
                            </span>
                          </Show>
                        </td>
                        <td class={styles.actionsCell}>
                          <button
                            type="button"
                            id={recordButtonId(action.id)}
                            data-testid={`shortcut-editor-record-${action.id}`}
                            onClick={() => startRecording(action.id)}
                          >
                            录制
                          </button>
                          <Show when={overridden()}>
                            <button
                              type="button"
                              data-testid={`shortcut-editor-reset-${action.id}`}
                              onClick={() => props.onResetOne(action.id)}
                            >
                              恢复默认
                            </button>
                          </Show>
                        </td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Show>
  );
}
